import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { zodResolver } from "@hookform/resolvers/zod";
import { timeOptions } from "@plasma/data/time";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod/v3";

import {
  employeeShiftsCreate,
  employeesGetAll,
  locationsGetAllMine,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";
import { DateInput } from "~/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { SelectCombobox } from "~/components/ui/select-combobox";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

// V4.1: Route-based modal for adding new employee shifts
export const Route = createFileRoute("/_authenticated/employees/shifts/new")({
  component: NewShiftModal,
});

const newShiftSchema = z.object({
  date: z.date({
    message: "Date is required",
  }),
  employeeId: z.string().min(1, "Employee is required"),
  locationId: z.string().min(1, "Location is required"),
  startTime: z.string().min(1, "Start time is required"),
  endTime: z.string().min(1, "End time is required"),
});

type NewShiftFormData = z.infer<typeof newShiftSchema>;

function NewShiftModal() {
  const navigate = useNavigate();

  const handleClose = () => {
    void navigate({ to: "/employees/shifts" });
  };

  const { data: employeesResult } = useQuery(
    convexQuery(employeesGetAll, {
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const employees = employeesResult?.page ?? [];
  const { data: locations = [] } = useQuery(
    convexQuery(locationsGetAllMine, {})
  );

  const createShiftMutation = useMutation({
    mutationFn: (params: {
      employeeId: Id<"employees">;
      locationId: Id<"locations">;
      startTime: string;
      endTime: string;
      date: string;
    }) => {
      return convexClient.mutation(employeeShiftsCreate, params);
    },
    onSuccess: (result) => {
      if (result) {
        ProductEvents.shiftCreated(result);
      }
      handleClose();
    },
  });

  const form = useForm<NewShiftFormData>({
    resolver: zodResolver(newShiftSchema),
    mode: "onChange",
    defaultValues: {
      employeeId: "",
      locationId: "",
      startTime: "",
      endTime: "",
    },
  });

  const handleSubmit = async (data: NewShiftFormData) => {
    if (createShiftMutation.isPending) {
      return;
    }
    await createShiftMutation.mutateAsync({
      employeeId: toConvexTableId("employees", data.employeeId),
      locationId: toConvexTableId("locations", data.locationId),
      startTime: data.startTime,
      endTime: data.endTime,
      date: `${data.date.getFullYear()}-${(data.date.getMonth() + 1).toString().padStart(2, "0")}-${data.date.getDate().toString().padStart(2, "0")}`,
    });
    form.reset();
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open>
      <DialogContent className="max-w-lg" noScrollPrevention>
        <DialogHeader>
          <DialogTitle className="h4 text-foreground text-center">
            Add Employee Shift
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-center">
            Record a new shift for an employee.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="date"
              render={() => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <DateInput name="date" placeholder="Select date..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee</FormLabel>
                  <FormControl>
                    <Combobox
                      dropdownClassName="w-105"
                      items={employees.map((e) => ({
                        value: e.id,
                        label: `${e.firstName} ${e.lastName}`,
                        keywords: [
                          e.firstName,
                          e.lastName,
                          `${e.firstName} ${e.lastName}`,
                        ],
                        imageUrl:
                          e.imageUrl ?? "/images/avatar-placeholder.svg",
                      }))}
                      onValueChange={field.onChange}
                      placeholder="Select employee..."
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="locationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Combobox
                      dropdownClassName="w-105"
                      items={locations.map((location) => ({
                        value: location.id,
                        label: location.name,
                        imageUrl:
                          location.logoUrl ??
                          "/images/location-logo-placeholder.svg",
                      }))}
                      onValueChange={field.onChange}
                      placeholder="Select location..."
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <SelectCombobox
                        onChange={field.onChange}
                        options={timeOptions}
                        value={field.value ?? null}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <SelectCombobox
                        onChange={field.onChange}
                        options={timeOptions}
                        value={field.value ?? null}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              className="w-full"
              disabled={createShiftMutation.isPending}
              size="lg"
              type="submit"
            >
              Add Shift
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
